import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, FilePlus, Smartphone, Globe, Calendar, Mail, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// --- INTERFACES ---
interface QuotationRequest {
  id: number;
  name: string;
  companyName?: string;
  email: string;
  phone: string;
  serviceType: string; // Backend se string aayega
  requestDate: string;
  status: string;
}

const QuotationRequests: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<QuotationRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<QuotationRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // --- FETCH REAL DATA ---
  const fetchRequests = async () => {
    setLoading(true);
    try {
        // const response = await fetch('http://localhost:2025/api/quotation/requests');
        const response = await fetch('https://www.projectinfotech.in/api/quotation/requests');
        if (response.ok) {
            const data = await response.json();
            setRequests(data);
            setFilteredRequests(data);
        }
    } catch (error) {
        console.error("Error fetching requests:", error);
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // --- SEARCH FILTER ---
  useEffect(() => {
    const lowerTerm = searchTerm.toLowerCase();
    const filtered = requests.filter(req => 
        req.name.toLowerCase().includes(lowerTerm) || 
        req.email.toLowerCase().includes(lowerTerm) ||
        (req.companyName && req.companyName.toLowerCase().includes(lowerTerm))
    );
    setFilteredRequests(filtered);
  }, [searchTerm, requests]);

  const handleCreateQuote = (request: QuotationRequest) => {
    // Data ko navigate ke saath bhejo
    navigate('/admin/quotation/create-quote', { state: { user: request } });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Quotation Requests</h1>
          <p className="text-gray-500 text-sm">Manage incoming business inquiries</p>
        </div>
        <button onClick={fetchRequests} className="p-2 bg-white border rounded-full hover:bg-gray-100 shadow-sm text-gray-600">
            <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* --- FILTERS --- */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex gap-4">
        <div className="relative flex-1">
            <Search className="absolute left-3 top-3 text-gray-400" size={20}/>
            <input 
                type="text" 
                placeholder="Search by name, email or company..." 
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
        </div>
      </div>

      {/* --- TABLE --- */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                    <th className="p-4 text-xs font-bold text-gray-500 uppercase">User Details</th>
                    <th className="p-4 text-xs font-bold text-gray-500 uppercase">Service Requested</th>
                    <th className="p-4 text-xs font-bold text-gray-500 uppercase">Date</th>
                    <th className="p-4 text-xs font-bold text-gray-500 uppercase text-right">Action</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
                {loading ? (
                    <tr><td colSpan={4} className="p-8 text-center text-gray-500">Loading requests...</td></tr>
                ) : filteredRequests.length === 0 ? (
                    <tr><td colSpan={4} className="p-8 text-center text-gray-400">No pending requests found.</td></tr>
                ) : (
                    filteredRequests.map((req) => (
                    <tr key={req.id} className="hover:bg-gray-50 transition">
                        <td className="p-4">
                            <div className="font-bold text-gray-800">{req.name}</div>
                            <div className="text-xs text-gray-500">{req.companyName || "No Company"}</div>
                            <div className="text-xs text-gray-400 flex items-center gap-1 mt-1"><Mail size={10}/> {req.email}</div>
                        </td>
                        <td className="p-4">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${req.serviceType.includes('App') ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                                {req.serviceType.includes('App') ? <Smartphone size={12}/> : <Globe size={12}/>}
                                {req.serviceType}
                            </span>
                        </td>
                        <td className="p-4 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                                <Calendar size={14} className="text-gray-400"/> {new Date(req.requestDate).toLocaleDateString()}
                            </div>
                        </td>
                        <td className="p-4 text-right">
                            <button 
                                onClick={() => handleCreateQuote(req)}
                                className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-red-700 transition shadow-md"
                            >
                                <FilePlus size={16}/> Create Quote
                            </button>
                        </td>
                    </tr>
                )))}
            </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuotationRequests;